import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { BRAND } from "../mock";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.trim() && /^\d{10}$/.test(form.mobile) && form.message.trim();

  const submit = (e) => {
    e.preventDefault();
    const existing = JSON.parse(localStorage.getItem("laundryma_enquiries") || "[]");
    localStorage.setItem("laundryma_enquiries", JSON.stringify([{ ...form, id: Date.now() }, ...existing]));
    toast({ title: "Message sent!", description: "Our team will get back to you shortly." });
    setForm({ name: "", email: "", mobile: "", message: "" });
  };

  return (
    <main>
      <section className="bg-gradient-to-br from-[#eef4fb] to-[#fdf3e6] py-14 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <Reveal>
            <span className="text-orange-brand font-bold text-sm uppercase tracking-wide">Get in Touch</span>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-navy font-head mt-2">We'd Love to Hear From You</h1>
            <p className="text-navy/70 mt-4">Questions, feedback or franchise enquiries — reach out and our team will respond quickly.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="space-y-5">
              <ContactCard icon={Phone} title="Call Us" value={BRAND.phoneDisplay} href={`tel:${BRAND.phone}`} />
              <ContactCard icon={MessageCircle} title="WhatsApp" value="Chat with us instantly" href={`https://wa.me/${BRAND.whatsapp}`} />
              <ContactCard icon={Mail} title="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
              <ContactCard icon={MapPin} title="Service Area" value="Serving 92+ cities across India" />
            </div>
          </Reveal>

          <Reveal delay={100}>
            <form onSubmit={submit} className="bg-[#f7f9fc] rounded-2xl p-6 sm:p-8 border border-gray-100 space-y-4">
              <Input label="Your Name" value={form.name} onChange={(v) => set("name", v)} placeholder="Full name" />
              <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="you@example.com" />
              <Input label="Mobile Number" value={form.mobile} maxLength={10} onChange={(v) => set("mobile", v.replace(/\D/g, ""))} placeholder="10-digit mobile" />
              <div>
                <label className="text-xs font-semibold text-navy/60 mb-1.5 block">Message</label>
                <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="How can we help you?"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-navy bg-white resize-none" />
              </div>
              <button disabled={!valid} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-orange-brand disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e0850f] text-white font-semibold py-3.5 rounded-xl transition-colors">
                Send Message <Send size={17} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

const ContactCard = ({ icon: Icon, title, value, href }) => {
  const inner = (
    <div className="flex items-center gap-4 bg-[#f7f9fc] hover:bg-white border border-transparent hover:border-orange-brand/20 hover:shadow-md rounded-2xl p-5 transition-all">
      <span className="w-12 h-12 shrink-0 rounded-xl bg-navy text-white flex items-center justify-center"><Icon size={22} /></span>
      <div>
        <p className="font-semibold text-navy font-head">{title}</p>
        <p className="text-sm text-navy/60">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a> : inner;
};

const Input = ({ label, value, onChange, placeholder, type = "text", maxLength }) => (
  <div>
    <label className="text-xs font-semibold text-navy/60 mb-1.5 block">{label}</label>
    <input type={type} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-navy bg-white" />
  </div>
);

export default ContactPage;
